import React, { useState, useEffect } from 'react';
import { Card, CardBody, Row, Col, Modal, Button } from 'react-bootstrap';
import DataTable, { TableColumn } from "react-data-table-component";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faSave, faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import ButtonComponent from '../../../components/ButtonComponent';
import ButtonComponentV2 from '../../../components/ButtonComponentV2';
import ControlledTextField from '../../../components/ControlledTextField';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateFeatureGroupMutation, useUpdateFeatureGroupMutation, useDeleteFeatureGroupMutation, useGetFeatureGroupsQuery, FeatureGroupItem } from '../../../redux/services/rolesPermissionService';
import TableLoadingComponent from '../../../components/TableLoadingComponent';
import EditButtonComponent from '../../../components/EditButtonComponent';
import Swal from "sweetalert2";

interface FeatureGroupFormData {
  displayName: string;
  description: string;
}

const FeatureGroups = () => {
  const [showModal, setShowModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState<FeatureGroupItem | null>(null);
  const [apiFeatureGroups, setApiFeatureGroups] = useState<FeatureGroupItem[]>([]);
  const [deletingGroupId, setDeletingGroupId] = useState<string | null>(null);

  const [createFeatureGroup, { isLoading: isCreating }] = useCreateFeatureGroupMutation();
  const [updateFeatureGroup, { isLoading: isUpdating }] = useUpdateFeatureGroupMutation();
  const [deleteFeatureGroup, { isLoading: isDeleting }] = useDeleteFeatureGroupMutation();
  const { data: featureGroupsData, isFetching, refetch: refetchFeatureGroups } = useGetFeatureGroupsQuery();

  const [rowsPerPage, setRowsPerPage] = useState(10);

  const isLoading = isCreating || isUpdating || isDeleting || isFetching;

  const { control, handleSubmit, reset, setValue, formState: { errors } } = useForm<FeatureGroupFormData>();

  // Sync local state from cached query
  useEffect(() => {
    if (featureGroupsData?.featureGroups) {
      setApiFeatureGroups(featureGroupsData.featureGroups);
    }
  }, [featureGroupsData]);

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
  };

  const handleAddGroup = () => {
    setEditingGroup(null);
    reset();
    setShowModal(true);
  };

  const handleEditGroup = (group: FeatureGroupItem) => {
    setEditingGroup(group);
    setValue('displayName', group.displayName || group.name || '');
    setValue('description', group.description);
    setShowModal(true);
  };

  const handleDeleteGroup = async (groupId: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#590B21",
      cancelButtonColor: "#ddd",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setDeletingGroupId(groupId);
        try {
          await deleteFeatureGroup({
            featureGroupId: groupId
          }).unwrap();

          // Cache invalidation will refetch; optimistically update local state
          setApiFeatureGroups(prev => prev.filter(group => group.id !== groupId));
          Swal.fire("Deleted!", "Feature group has been deleted.", "success");
          setDeletingGroupId(null);
        } catch (error) {
          console.error('Error deleting feature group:', error);
          Swal.fire(
            "Error!",
            "An error occurred while deleting the feature group.",
            "error"
          );
          setDeletingGroupId(null);
        }
      }
    });
  };

  const onSubmit = async (data: FeatureGroupFormData) => {
    try {
      if (editingGroup) {
        // Update existing group using RTK mutation
        const result = await updateFeatureGroup({
          id: editingGroup.id,
          displayName: data.displayName,
          description: data.description
        }).unwrap();

        // Update local state
        setApiFeatureGroups(apiFeatureGroups.map(group =>
          group.id === editingGroup.id
            ? { ...group, displayName: result.displayName, description: result.description }
            : group
        ));
        await refetchFeatureGroups();
        toast.success('Feature group updated successfully');
        setShowModal(false);
      } else {
        // Create new group using RTK mutation
        await createFeatureGroup({
          displayName: data.displayName,
          description: data.description
        }).unwrap();
        await refetchFeatureGroups();
        toast.success('Feature group created successfully');
        setShowModal(false);
      }
      setShowModal(false);
      reset();
    } catch (error: any) {
      console.error('Error:', error);
      if (error.status === 409) {
        toast.error(error.data)
      }
      else {
        toast.error('Something went wrong');
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    reset();
    setEditingGroup(null);
  };

  const columns: TableColumn<FeatureGroupItem>[] = [
    {
      name: <h6 className="fw-bold">#</h6>,
      cell: (row, index) => <span>{index + 1}</span>,
      sortable: false,
      width: '80px',
    },
    {
      name: <h6 className="fw-bold">Group Name</h6>,
      selector: (row) => row.displayName || row.name || '',
      cell: (row) => <strong>{row.displayName || row.name}</strong>,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Description</h6>,
      selector: (row) => row.description,
      cell: (row) => <span>{row.description}</span>,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Created Date</h6>,
      cell: (row) => <span>{new Date(row.createdAt._seconds * 1000).toLocaleDateString()}</span>,
      sortable: true,
    },
    {
      name: <h6 className="fw-bold">Actions</h6>,
      cell: (row) => (
        <div className="d-flex gap-2">
          <EditButtonComponent
            onClick={() => handleEditGroup(row)}
          />
          {deletingGroupId === row.id ? (
            <Button variant="light" disabled={true}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </Button>
          ) : (
            <Button variant="light" onClick={() => handleDeleteGroup(row.id)}>
              <FontAwesomeIcon icon={faTrash} title="Delete" color="#F24E1E" />
            </Button>
          )}
        </div>
      ),
      sortable: false,
      right: true,
    },
  ];

  return (
    <div>
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center px-4">
            <h4>Feature Groups Management</h4>
            <ButtonComponentV2
              titel="Add Feature Group"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddGroup}
              color="#fff"
              backgroundColor="#590B21"
            />
          </div>
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <CardBody>
              {isFetching ? (<TableLoadingComponent numColumns={4} />) : (
                <DataTable
                  columns={columns}
                  data={apiFeatureGroups}
                  responsive
                  pagination
                  highlightOnHover
                  pointerOnHover
                  paginationPerPage={rowsPerPage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                  // noDataComponent={<div>No feature groups found. Click "Add Feature Group" to create one.</div>}
                  noDataComponent={<div></div>}
                />)}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Feature Group Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingGroup ? 'Edit Feature Group' : 'Add New Feature Group'}</Modal.Title>
        </Modal.Header>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body>
            <Row className="mb-3">
              <Col>
                <ControlledTextField
                  label="Group Name"
                  placeholder="Enter feature group name"
                  inputName="displayName"
                  inputType="text"
                  control={control}
                  rules={{
                    required: 'Group name is required',
                    minLength: {
                      value: 2,
                      message: 'Group name must be at least 2 characters'
                    }
                  }}
                />
                {errors.displayName && (
                  <div className="text-danger mt-1">{errors.displayName.message}</div>
                )}
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <ControlledTextField
                  label="Description"
                  placeholder="Enter group description"
                  inputName="description"
                  inputType="text"
                  control={control}
                  rules={{
                    required: 'Description is required',
                    minLength: {
                      value: 2,
                      message: 'Description must be at least 2 characters'
                    }
                  }}
                />
                {errors.description && (
                  <div className="text-danger mt-1">{errors.description.message}</div>
                )}
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <ButtonComponent
              titel="Cancel"
              icon={<FontAwesomeIcon icon={faTimes} />}
              onClick={handleCloseModal}
              isDisabled={isLoading}
            />
            <ButtonComponent
              titel={isLoading ? "Saving..." : "Save"}
              icon={<FontAwesomeIcon icon={faSave} />}
              type="submit"
              isDisabled={isLoading}
            />
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default FeatureGroups;
